import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $recipientListStore, $recipientRecentStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { SortType } from '@shared/api/models';
import { type RecipientResponse, TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { RecentRecipientIcon } from './recentRecipientIcon';
import { RecipientDetails } from './recipientDetails';
import { ErrorMessage, Radio, SelectRecipient, Spinner, Text } from '@shared/widgets';
import { Constants } from '@shared/constants';

enum RecipientType {
  Existing = 'Existing Recipient',
  New = 'New Recipient',
}
const RECIPIENT_TYPE_OPTIONS = Object.entries(RecipientType).map(([, text]) => ({ text, value: text }));

interface RecipientSelectBlockProps {
  className?: string;
  type: TransferRecipientTypeEnum;
  isInternalAccount?: boolean;
  recipient?: RecipientResponse | null;
  onChangeRecipient: (val?: RecipientResponse | null) => void;
  isShowRecipientDetails: boolean;
  toggleRecipientDetails: () => void;
  error?: ReactNode;
}

export const RecipientSelectBlock = (props: RecipientSelectBlockProps) => {
  const { slug } = useParams<{ slug: 'sepa' }>();

  const recipientListStore = useUnit($recipientListStore.store);
  const recipientRecentStore = useUnit($recipientRecentStore.store);

  const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.Existing);
  const [searchValue, setSearchValue] = useState<string>('');

  const { setValue } = useFormContext();
  const { t } = useTranslation('payments');

  useEffect(() => {
    $recipientRecentStore.events.update({ Type: props.type, isInternalAccount: !!props.isInternalAccount });
  }, [props.isInternalAccount, props.type]);

  useEffect(() => {
    $recipientListStore.events.update({
      Count: 100,
      ReturnTotalCount: true,
      SearchQuery: searchValue,
      Type: props.type,
      isInternalAccount: !!props.isInternalAccount,
      SortType: SortType.Asc,
    });
  }, [searchValue, props.isInternalAccount, props.type]);

  const recipients = useMemo(() => {
    return recipientListStore.data.data.filter(
      (recipient) =>
        recipient.type === props.type &&
        (!slug || (Constants.SEPA_COUNTRIES_CODE.includes(recipient.bankCountryCode) && recipient.currency === 'EUR')),
    );
  }, [slug, props.type, recipientListStore.data.data]);
  const recentRecipients = useMemo(
    () =>
      recipientRecentStore.data.filter(
        (recipient) =>
          recipient.type === props.type &&
          (!slug || (Constants.SEPA_COUNTRIES_CODE.includes(recipient.bankCountryCode) && recipient.currency === 'EUR')),
      ),
    [slug, recipientRecentStore.data],
  );
  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Radio
        orientation='horizontal'
        options={RECIPIENT_TYPE_OPTIONS}
        value={recipientType}
        onChange={(value) => {
          const type = value as RecipientType;
          setRecipientType(type);
          switch (type) {
            case RecipientType.Existing:
              props.onChangeRecipient(undefined);
              setValue('currency', slug ? 'EUR' : undefined);
              break;
            case RecipientType.New:
              props.onChangeRecipient(null);
              break;
          }
        }}
      />
      {recipientType === RecipientType.Existing && (
        <div>
          <div>
            <SelectRecipient
              label={t('recipient.placeholders.transferTo')}
              items={recipients}
              value={props.recipient}
              onChange={props.onChangeRecipient}
              searchValue={searchValue}
              onSearch={setSearchValue}
            />
            <ErrorMessage text={props.error} />
          </div>
          {recipientRecentStore.loading && <Spinner />}
          {!recipientRecentStore.loading && !props.recipient && recentRecipients.some((r) => r.type === props.type) && (
            <div className='flex flex-col'>
              <Text className={cn('my-3', PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('recipient.title')}</Text>
              <div className='flex flex-row overflow-x-auto'>
                {recentRecipients.map((recipient, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'mr-2 flex cursor-pointer rounded-sm px-2 py-3',
                      PaletteUtils.isInsigniaPalette && ['bg-ice dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
                      PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
                    )}
                    onClick={() => props.onChangeRecipient(recipient)}
                  >
                    <RecentRecipientIcon
                      firstName={recipient.firstName}
                      lastName={recipient.lastName}
                      companyName={recipient.companyName}
                      accountNumber={recipient.accountNumber}
                      iban={recipient.iban}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {props.recipient && (
            <RecipientDetails
              recipient={props.recipient}
              toggleDetails={props.toggleRecipientDetails}
              isShowRecipientDetails={props.isShowRecipientDetails}
            />
          )}
        </div>
      )}
    </div>
  );
};
